export enum EAddEditCodeTab {
	GENERAL = 'general',
	CONDITIONS = 'conditions',
	LIMITS = 'limits',
	OWNERSHIP = 'ownership',
	COMBINING = 'combining',
	CODES = 'codes',
	HISTORY = 'history',
}

/** Liczba wpisów historii (activity-log) na stronę w dialogu szczegółów kodu. */
export const DISCOUNT_CODE_HISTORY_PER_PAGE = 10

export enum ENewDiscountType {
	PERCENTAGE = 'percentage',
	FIXED = 'fixed',
	FREE_DELIVERY = 'free_delivery',
	GRYZY = 'loyalty_points',
	CASHBACK = 'cashback_override',
	TIERED = 'tiered',
	FREE_DIET_DAYS = 'free_diet_days',
	GIFT = 'for_free',
}

export const NewDiscountTypeMap: Record<ENewDiscountType, string> = {
	[ENewDiscountType.PERCENTAGE]: 'Procentowy (%)',
	[ENewDiscountType.FIXED]: 'Kwotowy (PLN)',
	[ENewDiscountType.FREE_DELIVERY]: 'Darmowa dostawa',
	[ENewDiscountType.GRYZY]: 'Gryzy',
	[ENewDiscountType.CASHBACK]: 'Cashback',
	[ENewDiscountType.TIERED]: 'Rabat progowy',
	[ENewDiscountType.FREE_DIET_DAYS]: 'Darmowe dni diety',
	[ENewDiscountType.GIFT]: 'Gratis',
}

export const NewDiscountTypes = generateSelectOptions(ENewDiscountType, NewDiscountTypeMap)

export enum EGiftType {
	DIGITAL_REWARD = 'digital_reward',
	GADGET = 'gadget',
}

export const GiftTypeMap: Record<EGiftType, string> = {
	[EGiftType.DIGITAL_REWARD]: 'Nagroda cyfrowa',
	[EGiftType.GADGET]: 'Gadżet',
}

export const GiftTypes = generateSelectOptions(EGiftType, GiftTypeMap)

export enum ECodeGenerationType {
	SIMPLE = 'simple',
	RANDOM = 'random',
}

export enum ECodeCharType {
	DIGITS_ONLY = 'digits',
	LETTERS_ONLY = 'letters',
	MIXED = 'alphanumeric',
}

export const CodeCharTypeMap: Record<ECodeCharType, string> = {
	[ECodeCharType.DIGITS_ONLY]: 'Tylko cyfry (0-9)',
	[ECodeCharType.LETTERS_ONLY]: 'Tylko litery (A-Z)',
	[ECodeCharType.MIXED]: 'Mieszanka liter i cyfr (A-Z, 0-9)',
}

export const CodeCharTypes = generateSelectOptions(ECodeCharType, CodeCharTypeMap)

/* eslint-disable no-magic-numbers -- 4–8 are the allowed code character lengths per design spec */
export const CODE_CHAR_LENGTHS = [4, 5, 6, 7, 8].map(n => ({
	value: n,
	label: `${n} znak${n === 4 ? 'i' : 'ów'}`,
}))
export const DEFAULT_CODE_CHAR_LEN = 4
/* eslint-enable no-magic-numbers */

export enum EAccessType {
	ALL = 'all',
	SEGMENT = 'segment',
	SELECTED_EMAILS = 'selected_emails',
	DOMAIN = 'domain',
}

export const AccessTypeMap: Record<EAccessType, string> = {
	[EAccessType.ALL]: 'Wszyscy klienci',
	[EAccessType.SEGMENT]: 'Określone segmenty klientów',
	[EAccessType.SELECTED_EMAILS]: 'Określeni klienci',
	[EAccessType.DOMAIN]: 'Adresy e-mail z wybranej domeny',
}

export const AccessTypes = generateSelectOptions(EAccessType, AccessTypeMap)

export enum EProductScope {
	ALL = 'all',
	SELECTED = 'selected',
}

export enum EProductScopeGroup {
	OFFERS = 'oferty',
	CATEGORIES = 'kategorie',
	MEALS = 'posilki',
	TAGS = 'tagi',
}

/**
 * Flagi widoczności UI dla modułu kodów rabatowych.
 * Ustaw na `false` żeby tymczasowo ukryć funkcję (np. gdy testujemy lub brakuje obsługi UX).
 */
export const DISCOUNT_CODE_FLAGS = {
	/** Darmowa dostawa jako typ rabatu głównego oraz w tierach */
	freeDelivery: true,
	/** Gryzy (punkty lojalnościowe) jako typ rabatu głównego oraz w tierach */
	loyaltyPoints: true,
	/** Darmowe dni diety jako typ rabatu głównego oraz w tierach */
	freeDietDays: false,
	/** Typ progu w tierach: dni zamówienia (+ warunek ograniczenia dni w zwykłych kodach) */
	orderDaysThreshold: true,
	/** Grupy zakresu obowiązywania: posiłki i tagi */
	scopeMealsAndTags: true,
	/**
	 * Zakładka "Historia" w dialogu kodu rabatowego. Ukryta 2026-09-08: activity-log
	 * (`fetchDiscountCodeActivityLogs`) nie zwraca żadnych wpisów (prawdopodobnie brak
	 * integracji z serwisem auth w tym środowisku), a alternatywa - historia użycia kodu
	 * przez klientów (`fetchPromotionHistory`, `promotions/{coupon}/accesses`) - ma bug
	 * na backendzie: endpoint binduje `{coupon}` jako model `Coupon` po ID, ale frontend
	 * przekazuje `promotion.id` - dla ~97% kodów w bazie to dwa różne rekordy (potwierdzone
	 * zapytaniem SQL: tylko 1 z 34 promocji ma zgodne ID). Włącz z powrotem dopiero po
	 * naprawie jednego z tych dwóch mechanizmów.
	 */
	discountCodeHistoryTab: false,
} as const

export enum EThresholdConditionType {
	CART_VALUE = 'cart_value',
	ITEM_COUNT = 'item_count',
	MEAL_COUNT = 'meal_count',
	DIET_COUNT = 'diet_count',
	ORDER_DAYS = 'order_days',
}

export const ThresholdConditionTypeMap: Record<EThresholdConditionType, string> = {
	[EThresholdConditionType.CART_VALUE]: 'Wartość koszyka (PLN)',
	[EThresholdConditionType.ITEM_COUNT]: 'Liczba produktów w koszyku',
	[EThresholdConditionType.MEAL_COUNT]: 'Liczba posiłków w koszyku',
	[EThresholdConditionType.DIET_COUNT]: 'Liczba diet w koszyku',
	[EThresholdConditionType.ORDER_DAYS]: 'Dni zamówienia',
}

export const ThresholdConditionTypes = generateSelectOptions(EThresholdConditionType, ThresholdConditionTypeMap).filter(
	option =>
		option.value !== EThresholdConditionType.ITEM_COUNT &&
		(DISCOUNT_CODE_FLAGS.orderDaysThreshold || option.value !== EThresholdConditionType.ORDER_DAYS),
)

/** Returns true for any threshold type that maps to backend `item_count` (with or without variant). */
export const isItemCountType = (type: EThresholdConditionType | undefined): boolean =>
	type === EThresholdConditionType.ITEM_COUNT ||
	type === EThresholdConditionType.MEAL_COUNT ||
	type === EThresholdConditionType.DIET_COUNT

export enum ECodeType {
	STANDARD = 'standard',
	INFLUENCER = 'influencer',
	B2B = 'b2b',
}

export enum ECodeStatus {
	DRAFT = 'draft',
	PENDING = 'pending_approval',
	SCHEDULED = 'scheduled',
	ACTIVE = 'active',
	PAUSED = 'paused',
	COMPLETED = 'completed',
	ARCHIVED = 'archived',
}

export const CodeStatusMap: Record<ECodeStatus, string> = {
	[ECodeStatus.DRAFT]: 'Szkic',
	[ECodeStatus.PENDING]: 'Do akceptacji',
	[ECodeStatus.SCHEDULED]: 'Zaplanowany',
	[ECodeStatus.ACTIVE]: 'Aktywny',
	[ECodeStatus.PAUSED]: 'Wstrzymany',
	[ECodeStatus.COMPLETED]: 'Zakończony',
	[ECodeStatus.ARCHIVED]: 'Archiwalny',
}

export const CodeStatusTagType: Record<ECodeStatus, TagType> = {
	[ECodeStatus.DRAFT]: 'info',
	[ECodeStatus.PENDING]: 'on_hold',
	[ECodeStatus.SCHEDULED]: 'blue',
	[ECodeStatus.ACTIVE]: 'success',
	[ECodeStatus.PAUSED]: 'error',
	[ECodeStatus.COMPLETED]: 'finished',
	[ECodeStatus.ARCHIVED]: 'cancelled',
}

export const CodeStatusChipType: Record<ECodeStatus, EChipStatus> = {
	[ECodeStatus.DRAFT]: EChipStatus.DRAFT,
	[ECodeStatus.PENDING]: EChipStatus.PENDING,
	[ECodeStatus.SCHEDULED]: EChipStatus.SCHEDULED,
	[ECodeStatus.ACTIVE]: EChipStatus.ACTIVE,
	[ECodeStatus.PAUSED]: EChipStatus.PAUSED,
	[ECodeStatus.COMPLETED]: EChipStatus.COMPLETED,
	[ECodeStatus.ARCHIVED]: EChipStatus.ARCHIVED,
}

export const CodeStatuses = generateSelectOptions(ECodeStatus, CodeStatusMap)
export const CodeFilterStatuses = CodeStatuses.filter(
	s => s.value !== ECodeStatus.SCHEDULED && s.value !== ECodeStatus.PENDING,
)

export const CODE_STATUS_TRANSITIONS: Record<ECodeStatus, ECodeStatus[]> = {
	[ECodeStatus.DRAFT]: [ECodeStatus.ACTIVE, ECodeStatus.ARCHIVED],
	[ECodeStatus.PENDING]: [ECodeStatus.ACTIVE, ECodeStatus.DRAFT, ECodeStatus.ARCHIVED],
	[ECodeStatus.ACTIVE]: [ECodeStatus.PAUSED, ECodeStatus.COMPLETED, ECodeStatus.ARCHIVED],
	[ECodeStatus.PAUSED]: [ECodeStatus.ACTIVE, ECodeStatus.COMPLETED, ECodeStatus.ARCHIVED],
	[ECodeStatus.COMPLETED]: [ECodeStatus.ARCHIVED],
	[ECodeStatus.ARCHIVED]: [ECodeStatus.DRAFT, ECodeStatus.ACTIVE],
	[ECodeStatus.SCHEDULED]: [ECodeStatus.PAUSED, ECodeStatus.COMPLETED, ECodeStatus.ARCHIVED],
}

export const TYPES_WITH_NUMERIC_VALUE: ENewDiscountType[] = [
	ENewDiscountType.PERCENTAGE,
	ENewDiscountType.FIXED,
	ENewDiscountType.CASHBACK,
	ENewDiscountType.GRYZY,
	ENewDiscountType.FREE_DIET_DAYS,
]

export const DEFAULT_PRODUCT_SCOPE_GROUP = {
	enabled: false,
	mode: 'include' as const,
	selectedIds: [] as (number | string)[],
	minCount: null as number | null,
	maxCount: null as number | null,
}
